import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowUturnRightIcon,
  KeyIcon,
  PlusIcon,
  PowerIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  useCreateMailbox,
  useDeleteMailbox,
  useMailboxUsage,
  useMailboxes,
  useResetMailboxPassword,
  useUpdateMailbox,
} from "@/api/hooks";
import { ApiError, type Mailbox } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { formatBytes } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const createSchema = z.object({
  address: z.string().email(),
  password: z.string().min(10, "Min 10 characters"),
  quotaMb: z.coerce.number().int().nonnegative().optional(),
});

export default function MailboxesPage() {
  const { data, isLoading } = useMailboxes();
  const [resetFor, setResetFor] = useState<Mailbox | null>(null);
  const [deleteFor, setDeleteFor] = useState<Mailbox | null>(null);
  const [forwardFor, setForwardFor] = useState<Mailbox | null>(null);
  const [quotaFor, setQuotaFor] = useState<Mailbox | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">Mail</span>boxes
          </h1>
          <p className="mt-2 text-sm text-bitrefinery-text text-body">
            Create, disable, quota, and forward virtual mail users.
          </p>
        </div>
        <CreateMailboxDialog />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="min-w-[200px]">Usage</TableHead>
              <TableHead>Forwarding</TableHead>
              <TableHead className="w-[1%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data?.mailboxes.map((m) => (
              <MailboxRow
                key={m.address}
                mailbox={m}
                onReset={() => setResetFor(m)}
                onDelete={() => setDeleteFor(m)}
                onEditForward={() => setForwardFor(m)}
                onEditQuota={() => setQuotaFor(m)}
              />
            ))}
            {data && data.mailboxes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No mailboxes yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {resetFor && (
        <ResetPasswordDialog mailbox={resetFor} onOpenChange={(o) => !o && setResetFor(null)} />
      )}
      {deleteFor && (
        <DeleteMailboxDialog mailbox={deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)} />
      )}
      {forwardFor && (
        <ForwardingDialog mailbox={forwardFor} onOpenChange={(o) => !o && setForwardFor(null)} />
      )}
      {quotaFor && (
        <QuotaDialog mailbox={quotaFor} onOpenChange={(o) => !o && setQuotaFor(null)} />
      )}
    </div>
  );
}

function MailboxRow({
  mailbox,
  onReset,
  onDelete,
  onEditForward,
  onEditQuota,
}: {
  mailbox: Mailbox;
  onReset: () => void;
  onDelete: () => void;
  onEditForward: () => void;
  onEditQuota: () => void;
}) {
  const update = useUpdateMailbox();
  const usage = useMailboxUsage(mailbox.active ? mailbox.address : null);
  const quotaBytes = Number(mailbox.quota_bytes);

  return (
    <TableRow>
      <TableCell>
        <div className="font-mono text-sm">{mailbox.address}</div>
        <div className="text-xs text-muted-foreground">{mailbox.domain}</div>
      </TableCell>
      <TableCell>
        <Badge variant={mailbox.active ? "secondary" : "outline"}>
          {mailbox.active ? "active" : "disabled"}
        </Badge>
      </TableCell>
      <TableCell>
        <UsageCell
          used={usage.data?.bytes ?? null}
          limit={usage.data?.limitBytes ?? (quotaBytes > 0 ? quotaBytes : null)}
          loading={usage.isLoading}
          onClick={onEditQuota}
        />
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={onEditForward}
          className="group inline-flex items-center gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-accent"
        >
          {mailbox.forward_to ? (
            <>
              <span className="font-mono">{mailbox.forward_to}</span>
              <Badge variant="outline" className="text-[10px]">
                {mailbox.forward_keep_copy ? "+ keep copy" : "forward only"}
              </Badge>
            </>
          ) : (
            <span className="text-muted-foreground group-hover:text-foreground">
              (none — click to set)
            </span>
          )}
        </button>
      </TableCell>
      <TableCell className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" title="Reset password" onClick={onReset}>
          <KeyIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Forwarding"
          onClick={onEditForward}
        >
          <ArrowUturnRightIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title={mailbox.active ? "Disable" : "Enable"}
          onClick={() =>
            update.mutate({ address: mailbox.address, active: !mailbox.active })
          }
        >
          <PowerIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Delete"
          onClick={onDelete}
          disabled={!mailbox.active}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function UsageCell({
  used,
  limit,
  loading,
  onClick,
}: {
  used: number | null;
  limit: number | null;
  loading: boolean;
  onClick: () => void;
}) {
  if (loading && used === null) {
    return <div className="text-xs text-muted-foreground">…</div>;
  }
  const usedStr = used === null ? "?" : formatBytes(used);
  const limitStr = !limit || limit === 0 ? "unlimited" : formatBytes(limit);
  const pct =
    used !== null && limit && limit > 0 ? Math.min(100, (used / limit) * 100) : null;
  const tone = pct === null ? "brand" : pct >= 95 ? "danger" : pct >= 80 ? "warn" : "brand";
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full max-w-[220px] space-y-1 rounded px-1 text-left hover:bg-accent"
      title="Click to change quota"
    >
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono">{usedStr}</span>
        <span className="text-muted-foreground">{limitStr}</span>
      </div>
      <Progress percent={pct ?? 0} tone={tone} />
    </button>
  );
}

function QuotaDialog({
  mailbox,
  onOpenChange,
}: {
  mailbox: Mailbox;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateMailbox();
  const initial = Math.round(Number(mailbox.quota_bytes) / 1024 / 1024);
  const form = useForm<{ quotaMb: number }>({
    defaultValues: { quotaMb: initial },
    resolver: zodResolver(z.object({ quotaMb: z.coerce.number().int().nonnegative() })),
  });
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set quota</DialogTitle>
          <DialogDescription>
            Maximum mailbox size for <span className="font-mono">{mailbox.address}</span>, in
            megabytes. <b>0 = unlimited.</b> Dovecot rejects incoming messages with 552 once
            the limit is hit (after a 10% grace).
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async ({ quotaMb }) => {
            await update.mutateAsync({
              address: mailbox.address,
              quotaBytes: quotaMb * 1024 * 1024,
            });
            onOpenChange(false);
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="q">Quota (MB)</Label>
            <Input id="q" type="number" min={0} {...form.register("quotaMb")} />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ForwardingDialog({
  mailbox,
  onOpenChange,
}: {
  mailbox: Mailbox;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateMailbox();
  const [target, setTarget] = useState(mailbox.forward_to ?? "");
  const [keep, setKeep] = useState(mailbox.forward_keep_copy);
  const [err, setErr] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Forwarding</DialogTitle>
          <DialogDescription>
            Incoming mail for <span className="font-mono">{mailbox.address}</span> is
            forwarded to the address below. When "Keep a copy locally" is on, a copy is
            still delivered to this Maildir; otherwise the local mailbox is bypassed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fwd">Forward to</Label>
            <Input
              id="fwd"
              placeholder="external@example.com"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to disable forwarding.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="keep">Keep a copy locally</Label>
              <p className="text-xs text-muted-foreground">
                Off = forward only; the local Maildir is skipped.
              </p>
            </div>
            <Switch id="keep" checked={keep} onCheckedChange={setKeep} />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setErr(null);
              const clean = target.trim().toLowerCase();
              try {
                await update.mutateAsync({
                  address: mailbox.address,
                  forwardTo: clean ? clean : null,
                  forwardKeepCopy: keep,
                });
                onOpenChange(false);
              } catch (e) {
                setErr(e instanceof ApiError ? e.code : "failed");
              }
            }}
            disabled={update.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateMailboxDialog() {
  const [open, setOpen] = useState(false);
  const create = useCreateMailbox();
  const form = useForm<z.infer<typeof createSchema>>({ resolver: zodResolver(createSchema) });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          New mailbox
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create mailbox</DialogTitle>
          <DialogDescription>
            The domain must already exist. Password is hashed (SHA512-CRYPT) before storage.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (values) => {
            try {
              await create.mutateAsync({
                address: values.address,
                password: values.password,
                quotaBytes: values.quotaMb ? values.quotaMb * 1024 * 1024 : undefined,
              });
              form.reset();
              setOpen(false);
            } catch (err) {
              const msg = err instanceof ApiError ? err.code : "failed";
              form.setError("address", { message: msg });
            }
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="address">Email address</Label>
            <Input id="address" placeholder="alice@aigrammys.com" {...form.register("address")} />
            {form.formState.errors.address && (
              <p className="text-xs text-destructive">
                {form.formState.errors.address.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Initial password</Label>
            <Input id="password" type="password" {...form.register("password")} />
            {form.formState.errors.password && (
              <p className="text-xs text-destructive">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quotaMb">Quota (MB, 0 = unlimited)</Label>
            <Input id="quotaMb" type="number" defaultValue={0} {...form.register("quotaMb")} />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  mailbox,
  onOpenChange,
}: {
  mailbox: Mailbox;
  onOpenChange: (open: boolean) => void;
}) {
  const reset = useResetMailboxPassword();
  const form = useForm<{ newPassword: string }>({
    resolver: zodResolver(z.object({ newPassword: z.string().min(10) })),
  });
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Sets a new password for <span className="font-mono">{mailbox.address}</span>.
            The user must log in with the new password.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async ({ newPassword }) => {
            await reset.mutateAsync({ address: mailbox.address, newPassword });
            onOpenChange(false);
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <Input id="new" type="password" {...form.register("newPassword")} />
            {form.formState.errors.newPassword && (
              <p className="text-xs text-destructive">
                {form.formState.errors.newPassword.message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={reset.isPending}>
              {reset.isPending ? "Saving…" : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteMailboxDialog({
  mailbox,
  onOpenChange,
}: {
  mailbox: Mailbox;
  onOpenChange: (open: boolean) => void;
}) {
  const del = useDeleteMailbox();
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disable mailbox?</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{mailbox.address}</span> will stop receiving and accepting
            mail. Stored messages stay on disk until purged (30 days). You can re-enable anytime.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={async () => {
              await del.mutateAsync(mailbox.address);
              onOpenChange(false);
            }}
            disabled={del.isPending}
          >
            {del.isPending ? "Disabling…" : "Disable"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

